import { useEffect, useState } from 'hono/jsx';
import { SiteShell } from '../../components/SiteShell';
import { Spinner } from '../../components/Spinner';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { useSite } from '../../lib/site';

interface Version { id: string; timestamp: string; isCurrent: boolean; prompt?: string; }

function fmt(timestamp: string, index: number): { label: string; dateStr: string; timeStr: string } {
  const date = new Date(timestamp), now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86_400_000);
  let label: string;
  if (index === 0) label = 'Current Version';
  else if (diffDays === 0) label = 'Earlier today';
  else if (diffDays === 1) label = 'Yesterday';
  else if (diffDays < 7) label = diffDays + ' days ago';
  else if (diffDays < 14) label = 'Last week';
  else if (diffDays < 30) label = Math.floor(diffDays / 7) + ' weeks ago';
  else label = Math.floor(diffDays / 30) + ' months ago';
  return {
    label,
    dateStr: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
    timeStr: date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
  };
}

export function SiteVersions({ siteId }: { siteId: string }) {
  const { site, loading: siteLoading } = useSite(siteId);
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [rolling, setRolling] = useState<string | null>(null);

  async function loadVersions() {
    setLoading(true); setError(false);
    try {
      const r = await api('/sites/' + siteId + '/versions?_t=' + Date.now(), { cache: 'no-store' });
      if (!r.ok) throw new Error('Failed to load versions');
      const data = await r.json();
      setVersions(data.versions || []);
    } catch { setError(true); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadVersions(); }, [siteId]);

  function toggleExpand(id: string) {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function onRollback(versionId: string) {
    if (!confirm('Restore this version?\n\nThis will make it the new preview. Your current preview will be replaced.')) return;
    setRolling(versionId);
    try {
      const r = await api('/sites/' + siteId + '/rollback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ versionId }),
      });
      const result = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((result as any).error || 'Failed to restore version');
      showToast('info', 'Restoring Version', 'Please wait while we restore your preview…');
      let attempts = 0, completed = false;
      while (attempts < 60 && !completed) {
        await new Promise((res) => setTimeout(res, 2000)); attempts++;
        try {
          const sr = await api('/sites/' + siteId + '/spark-pro/status');
          if (sr.ok) {
            const s = await sr.json();
            if (s.status === 'complete' || s.status === 'live') { completed = true; break; }
            if (s.status === 'failed' || s.status === 'error') throw new Error(s.error || 'Rollback failed');
          }
        } catch (e) { /* keep polling */ }
      }
      showToast(completed ? 'success' : 'warning', completed ? 'Version Restored' : 'Restore In Progress',
        completed ? 'The preview has been updated. Use Publish to push it live.' : 'The restore is taking longer than expected. Check back shortly.');
      await loadVersions();
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to restore version');
    } finally {
      setRolling(null);
    }
  }

  return (
    <SiteShell site={site} loading={siteLoading} active="versions">
      <div class="bg-dark-800 border border-slate-700/50 rounded-xl">
        <div class="px-6 py-4 border-b border-slate-700/50"><h2 class="text-lg font-semibold text-slate-100">Version History</h2></div>
        <div class="p-4">
          {loading ? (
            <div class="flex flex-col items-center justify-center py-8"><Spinner /><p class="text-slate-400 mt-4 text-sm">Loading versions…</p></div>
          ) : error ? (
            <div class="text-center py-8">
              <svg class="w-12 h-12 mx-auto text-red-400/50 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
              <p class="text-slate-400 text-sm">Failed to load versions</p>
              <button type="button" onClick={loadVersions} class="mt-3 text-cyan-400 hover:text-cyan-300 text-sm">Try again</button>
            </div>
          ) : versions.length === 0 ? (
            <div class="text-center py-8">
              <svg class="w-12 h-12 mx-auto text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              <p class="text-slate-400 text-sm">No previous versions found</p>
              <p class="text-slate-500 text-xs mt-1">Versions are created when you make updates</p>
            </div>
          ) : (
            <div class="space-y-2">
              {versions.map((v, index) => {
                const f = fmt(v.timestamp, index);
                const open = expanded.has(v.id);
                return (
                  <div class={`rounded-lg border ${v.isCurrent ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-slate-700/50 hover:border-slate-600 hover:bg-dark-700/50'} transition-colors`}>
                    <div class="flex items-center justify-between p-3">
                      <div class="flex items-center gap-3 min-w-0">
                        <div class={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${v.isCurrent ? 'bg-cyan-500/20 text-cyan-400' : 'bg-dark-700 text-slate-400'}`}>
                          {v.isCurrent ? (
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                          ) : (
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                          )}
                        </div>
                        <div class="min-w-0">
                          <p class={`text-sm font-medium ${v.isCurrent ? 'text-cyan-400' : 'text-slate-200'}`}>{f.label}</p>
                          <p class="text-xs text-slate-500">{f.dateStr} at {f.timeStr}</p>
                        </div>
                      </div>
                      <div class="flex items-center gap-2 flex-shrink-0">
                        {v.prompt && (
                          <button type="button" onClick={() => toggleExpand(v.id)} class="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-dark-700 rounded transition-colors" title="View build prompt">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                          </button>
                        )}
                        {!v.isCurrent ? (
                          <button type="button" onClick={() => onRollback(v.id)} disabled={rolling === v.id} class="text-sm text-slate-400 hover:text-cyan-400 px-3 py-1.5 rounded border border-slate-600/50 hover:border-cyan-500/30 transition-colors disabled:opacity-60">
                            {rolling === v.id ? 'Restoring…' : 'Restore'}
                          </button>
                        ) : (
                          <span class="text-xs text-cyan-400/70 px-2 py-1 bg-cyan-500/10 rounded">Active</span>
                        )}
                      </div>
                    </div>
                    {v.prompt && open && (
                      <div class="px-3 pb-3">
                        <div class="bg-dark-900/50 border border-slate-700/30 rounded-lg p-3">
                          <p class="text-xs font-medium text-slate-400 mb-1">Build Prompt</p>
                          <p class="text-sm text-slate-300 leading-relaxed">{v.prompt}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {versions.length > 0 && (
          <div class="px-6 py-4 border-t border-slate-700/50">
            <p class="text-xs text-slate-500 text-center">
              <svg class="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              Restoring a version makes it the new preview
            </p>
          </div>
        )}
      </div>
    </SiteShell>
  );
}
